// SignInFlow.tsx · v12.10.11.1 (#1460 Contact support: same person again -> same SR + Add to SR; 3 a day) · 2026-10-09
// SignInFlow.tsx · v12.10.11.1 (#1462 the invite page is pre-filled with the name / mobile the inviter or Step 5 entered) · 2026-10-09
// SignInFlow.tsx · v12.10.11.1 (#1470 typed identifier shown; send failure for a real account says "Technical error…") · 2026-10-09
// SignInFlow.tsx · v12.10.9.4 (#1342 F9 password rules) · 2026-10-07 · VoiceSpectrum AI
// SignInFlow.tsx · v12.10.9.3 (#1312 Contact support + badges below the card) · 2026-10-07
// VoiceSpectrum.Web.T2 · v12.10.9.1 (#1271 notice text · #1273 Keep tick remembered · #1281 newer version loads at sign-in · #1275 Contact support form) · 2026-10-06
// VoiceSpectrum.Web.T2 · v12.10.9 (Security Phase 1 – #1255/#1256/#1257) · 2026-10-06
// SignInFlow.tsx - the sign-in state machine (Login_Prototype_v1_2 L1–L13) over CONTRACT v1.0 §1.
//
//   /login            L1 sign in → L2 (401 invalid) · L3 (423 locked) · L9 (403 disabled) · L1a code sign-in → L4
//                     then by LoginResult.step: profile → L13 · second → L4 / L4a / L4b · setup → L5 → L5a | L4 → L5b
//                     done (+ mustChangePassword) → L8 → L7 → in.   L12 banner when a session ended (auth.tsx notice).
//                     L6 forgot → L6b → (sms) L4 code → L7 → back to L1 "Password changed".
//   /reset-password   ?token= → L7 → /login "Password changed".
//   /invite           ?token= → L10 (L7 with name fields) → LoginResult (normally setup → L5) · 410 → L11.
//
// Never reveals whether an account exists: 401 invalid shows the same words for an unknown account and a wrong password,
// code sign-in and forgot-password always move on. No person is named anywhere before sign-in.
// Code sign-in (S30): the second step must come from a different place - the server's LoginResult.methods already
// leaves out the channel just used, so this screen only ever offers what it returns.
import { normMix, type CharMix } from '@shared-ui/commonPasswords';   // v12.10.9.4 (#1342)
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../../lib/api';
import { useAuth, takeSignedOutNotice, type SignedOutNotice } from '../../lib/auth';
import { newerVersionOut, reloadIfNewer } from '../../lib/useVersionCheck';
import { authApi, authError, saveTrust, type CodeChannel, type LoginResult, type SecondMethod, type SetupMethod } from '../../lib/authApi';
import { toE164 } from '../../lib/phone';
import { Banner, BelowCard, LoginFrame, OUTAGE_MSG, resolveTenantDisplayName, resolveTenantSlug } from './parts';
import {
  BackupCodesView, BackupView, CodeView, DisabledView, ForgotSentView, ForgotView, InviteProblemView, LoadingView, LockedView,
  NewPasswordView, ProfileView, RequiredView, SetupTotpView, SetupView, SignInView, SupportSentView, SupportView, TotpView,
  type CodePurpose, type PwPurpose, type SupportFields, type SupportOutcome,
} from './views';
import '../../styles/login.css';

type View =
  | 'signin' | 'locked' | 'code' | 'totp' | 'backup' | 'setup' | 'setup_totp' | 'backup_show' | 'profile'
  | 'forgot' | 'forgot_sent' | 'newpw' | 'required' | 'disabled' | 'invite_loading' | 'invite_problem'
  | 'support' | 'support_sent';   // v12.10.9.1 (#1275)

type Notice = { kind: 'bad' | 'info' | 'warn' | 'ok'; text: ReactNode } | null;

export type SignInMode = 'login' | 'reset' | 'invite';

const DEFAULT_LOCK_SECONDS = 15 * 60;
const DEFAULT_RESEND_SECONDS = 30;
const TIMED_OUT = 'Your sign-in took too long, so we stopped it. Sign in again.';

// React StrictMode runs state initialisers twice in development; the notice is read-and-cleared, so remember it briefly.
let lastNotice: { at: number; v: SignedOutNotice | null } | null = null;
function takeNoticeOnce(): SignedOutNotice | null {
  if (lastNotice && Date.now() - lastNotice.at < 1500) return lastNotice.v;
  const v = takeSignedOutNotice();
  lastNotice = { at: Date.now(), v };
  return v;
}

/** L12 wording per reason. */
function signedOutText(n: SignedOutNotice): string {
  // v12.10.9.1 (#1271, Tushar 10/6): one short pattern for every reason - what happened, then what to do.
  switch (n.reason) {
    case 'idle':
      return n.minutes
        ? `Signed out after ${n.minutes} ${n.minutes === 1 ? 'minute' : 'minutes'} of inactivity. Sign in to continue.`
        : 'Signed out after a period of inactivity. Sign in to continue.';
    case 'absolute':
      return n.hours
        ? `Signed out after ${n.hours} ${n.hours === 1 ? 'hour' : 'hours'}. Sign in to continue.`
        : 'Signed out after the longest session allowed. Sign in to continue.';
    case 'revoked':
    case 'access_changed':
      return 'You were signed out. Sign in again to continue.';
    default:
      return 'Your session ended. Sign in to continue.';
  }
}

/** Email as typed; a 10-digit US/Canada number (any formatting) as E.164. */
function normalizeIdentifier(raw: string): string {
  const t = raw.trim();
  if (t.includes('@')) return t;
  return toE164(t) ?? t;
}

function tooManyText(seconds: number | undefined): string {
  return seconds && seconds > 0 ? `Too many codes were requested. Try again in ${Math.ceil(seconds / 60)} ${Math.ceil(seconds / 60) === 1 ? 'minute' : 'minutes'}.` : 'Too many codes were requested. Try again later.';
}

export default function SignInFlow({ mode }: { mode: SignInMode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const { login } = useAuth();
  const tenantSlug = useMemo(() => resolveTenantSlug(), []);
  const urlToken = params.get('token')?.trim() || '';

  const fromState = (location.state as {
    from?: { pathname: string; search?: string }; notice?: string; noticeKind?: 'bad' | 'info' | 'ok'; noticeText?: string;
  } | null) ?? null;
  const from = fromState?.from ? `${fromState.from.pathname}${fromState.from.search ?? ''}` : '/';

  // ── state ────────────────────────────────────────────────────────────────────────────────────────────────────────
  const [org, setOrg] = useState(resolveTenantDisplayName);
  const [view, setView] = useState<View>(() => {
    if (mode === 'invite') return urlToken ? 'invite_loading' : 'invite_problem';
    if (mode === 'reset') return urlToken ? 'newpw' : 'forgot';
    return 'signin';
  });
  const [notice, setNotice] = useState<Notice>(() => {
    if (mode === 'reset' && !urlToken) return { kind: 'bad', text: 'This reset link isn’t complete. Enter your email or mobile to get a new one.' };
    if (mode !== 'login') return null;
    if (fromState?.noticeText) return { kind: fromState.noticeKind ?? 'info', text: fromState.noticeText };
    if (fromState?.notice === 'pwchanged') return { kind: 'ok', text: 'Password changed. You were signed out on your other devices. Sign in with your new password.' };
    const n = takeNoticeOnce();
    return n ? { kind: 'info', text: signedOutText(n) } : null;
  });
  const [busy, setBusy] = useState(false);
  const [codeBusy, setCodeBusy] = useState<CodeChannel | null>(null);

  // L1/L2
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  // v12.10.9.1 (#1273): the Keep me signed in box comes back the way this browser left it.
  const [keep, setKeepState] = useState<boolean>(() => { try { return localStorage.getItem('vs_t2_keep') === '1'; } catch { return false; } });
  const setKeep = (v: boolean) => { setKeepState(v); try { localStorage.setItem('vs_t2_keep', v ? '1' : '0'); } catch { /* blocked */ } };
  // v12.10.9.1 (#1288, S47): "Don't ask again on this browser for N days" on the 2-step screens - ticked by default.
  const [trustDays, setTrustDays] = useState(0);
  const [trustOn, setTrustOn] = useState(true);
  const trust = trustDays > 0 ? { days: trustDays, on: trustOn, set: setTrustOn } : undefined;
  const wantTrust = trustDays > 0 && trustOn;
  const [mismatch, setMismatch] = useState(false);
  const [errTick, setErrTick] = useState(0);
  const typedPasswordRef = useRef<string | null>(null);   // the password that signed in (L8 change-password needs it)

  // L3
  const [lockUntil, setLockUntil] = useState(0);

  // step machine
  const [pendingToken, setPendingToken] = useState('');
  const [methods, setMethods] = useState<SecondMethod[]>([]);
  const [legacyMfa, setLegacyMfa] = useState('');      // old platform-admin preMfaToken
  const [fromCode, setFromCode] = useState<CodeChannel | null>(null);
  const [setupInfo, setSetupInfo] = useState<{ canSkip: boolean; graceDaysLeft?: number; enforceOn?: string; methods: SetupMethod[] }>({ canSkip: false, methods: ['totp', 'sms'] });
  const [totp, setTotp] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [afterCodes, setAfterCodes] = useState<LoginResult | null>(null);
  const [doneToken, setDoneToken] = useState('');
  const [profileMobile, setProfileMobile] = useState('');
  const [names, setNames] = useState<{ first: string; last: string }>({ first: '', last: '' });

  // code view (L4)
  const [codePurpose, setCodePurpose] = useState<CodePurpose>('second');
  const [codeChannel, setCodeChannel] = useState<CodeChannel>('sms');
  const [codeMasked, setCodeMasked] = useState<string | null>(null);
  const [resendAt, setResendAt] = useState(0);
  const [resent, setResent] = useState(false);
  const [codeErr, setCodeErr] = useState<string | null>(null);
  const [codeKey, setCodeKey] = useState(0);
  const [stepError, setStepError] = useState<string | null>(null);   // L5 / L13 errors

  // forgot / reset (L6 / L7)
  const [resetVia, setResetVia] = useState<'email' | 'sms'>('email');
  const [resetIdentifier, setResetIdentifier] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [pwPurpose, setPwPurpose] = useState<PwPurpose>(mode === 'invite' ? 'invite' : 'reset_token');
  const [rules, setRules] = useState<{ minLength: number; reuse: number; charMix: CharMix; context: (string | null)[] }>({ minLength: 12, reuse: 5, charMix: 'any', context: [] });   // v12.10.9.4 (#1342)
  const [rejections, setRejections] = useState<{ password: string; failed: string[] }[]>([]);
  const [pwError, setPwError] = useState<ReactNode>(null);

  // invite (L10 / L11)
  const [invite, setInvite] = useState<{ orgName: string; groupNames: string[]; identifier: string; firstName?: string | null; lastName?: string | null; mobile?: string | null } | null>(null);   // v12.10.11.1 (#1462) names / mobile to pre-fill
  const [inviteProblem, setInviteProblem] = useState<'expired' | 'invalid' | 'error'>('invalid');
  const [inviteTry, setInviteTry] = useState(0);
  const inviteOrgRef = useRef(false);   // the invite's org name wins over the address lookup

  // ── effects ──────────────────────────────────────────────────────────────────────────────────────────────────────
  // Organization name for the badge (from the address). Sign-in stays usable when the lookup fails.
  useEffect(() => {
    if (!tenantSlug) return;
    let cancelled = false;
    api.get<{ slug: string | null; businessName: string | null }>(`/public/tenant-login-context?slug=${encodeURIComponent(tenantSlug)}`)
      .then((c) => {
        const name = c?.businessName?.trim();
        if (!name || cancelled) return;
        try { sessionStorage.setItem('vs.tenantDisplayName', name); } catch { /* blocked */ }
        if (!inviteOrgRef.current) setOrg(name);
      })
      .catch(() => { /* keep the slug-derived name */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantSlug]);

  // Password rules for L7 (length, last N). Defaults stay if the endpoint is unavailable.
  useEffect(() => {
    if (view !== 'newpw') return;
    let cancelled = false;
    authApi.passwordRules(tenantSlug)
      .then((r) => { if (!cancelled && r && r.minLength > 0) setRules({ minLength: r.minLength, reuse: Math.max(0, r.reuse ?? 0), charMix: normMix(r.charMix, r.complexity), context: r.context ?? [] }); })
      .catch(() => { /* defaults */ });
    return () => { cancelled = true; };
  }, [view, tenantSlug]);

  // /invite?token= → look the invite up (L10) or explain why not (L11).
  useEffect(() => {
    if (mode !== 'invite' || !urlToken) return;
    let cancelled = false;
    setView('invite_loading');
    authApi.invite(urlToken)
      .then((info) => {
        if (cancelled) return;
        setInvite(info);
        if (info.orgName?.trim()) { inviteOrgRef.current = true; setOrg(info.orgName.trim()); }
        setPwPurpose('invite');
        setView('newpw');
      })
      .catch((err) => {
        if (cancelled) return;
        const { status, body } = authError(err);
        setInviteProblem(status === 410 ? (body.error === 'expired' ? 'expired' : 'invalid') : status === 404 || status === 400 ? 'invalid' : 'error');
        setView('invite_problem');
      });
    return () => { cancelled = true; };
  }, [mode, urlToken, inviteTry]);

  // ── helpers ──────────────────────────────────────────────────────────────────────────────────────────────────────
  const finish = useCallback((token: string) => {
    login(token);
    try { sessionStorage.removeItem('vs.tenantSlug'); } catch { /* blocked */ }
    typedPasswordRef.current = null;
    const target = mode === 'login' ? from : '/';
    // v12.10.9.1 (#1281): a newer version is out -> load it now (a full page load), nothing is in progress yet.
    void newerVersionOut().then((newer) => {
      if (newer) window.location.assign(target);
      else navigate(target, { replace: true });
    });
  }, [login, navigate, from, mode]);

  // v12.10.9.1 (#1281): the sign-in page itself never runs an old version.
  useEffect(() => { void reloadIfNewer(); }, []);

  function backToSignIn(n: { kind: 'bad' | 'info' | 'ok'; text: string } | null = null) {
    typedPasswordRef.current = null;
    if (mode !== 'login') {
      navigate('/login', { replace: true, state: n ? { noticeKind: n.kind, noticeText: n.text } : null });
      return;
    }
    setView('signin');
    setNotice(n);
    setMismatch(false);
    setPassword('');
    setPendingToken(''); setLegacyMfa(''); setFromCode(null); setDoneToken('');
    setCodeErr(null); setStepError(null); setBusy(false);
  }

  function goLocked(seconds: number | undefined) {
    setLockUntil(Date.now() + Math.max(1, seconds && seconds > 0 ? seconds : DEFAULT_LOCK_SECONDS) * 1000);
    setView('locked');
  }

  function openCode(purpose: CodePurpose, channel: CodeChannel, masked: string | null, resendIn?: number) {
    setCodePurpose(purpose); setCodeChannel(channel); setCodeMasked(masked);
    setResendAt(Date.now() + (resendIn ?? DEFAULT_RESEND_SECONDS) * 1000);
    setResent(false); setCodeErr(null); setCodeKey((k) => k + 1);
    setView('code');
  }

  /** A failed code / step call → the right state. Returns the inline message for a wrong code, or null when it moved on. */
  function codeFailure(err: unknown, wrongText: string): void {
    const { status, body } = authError(err);
    if (body.error === 'send_failed') { setCodeErr('Technical error while sending the code. Please try another method.'); return; }   // v12.10.11.1 (#1470)
    if (status === 401 && body.error === 'bad_code') {
      const n = body.triesLeft;
      setCodeErr(typeof n === 'number' ? `${wrongText} ${n} ${n === 1 ? 'try' : 'tries'} left.` : wrongText);
      setCodeKey((k) => k + 1);
      return;
    }
    if (status === 429 && body.error === 'too_many_attempts') { setCodeErr(String(body.message ?? 'Too many sign-in attempts from this network. Try again later.')); setCodeKey((k) => k + 1); return; }   // v12.10.9.1 (#1296)
    if (status === 429) { goLocked(body.retryInSeconds ?? body.secondsLeft); return; }
    if (status === 423) { goLocked(body.secondsLeft ?? body.retryInSeconds); return; }
    if (status === 403 && body.error === 'disabled') { setView('disabled'); return; }
    if (status === 401 || status === 410) { backToSignIn({ kind: 'bad', text: TIMED_OUT }); return; }
    if (status === 400) { setCodeErr(wrongText); setCodeKey((k) => k + 1); return; }
    setCodeErr(OUTAGE_MSG); setCodeKey((k) => k + 1);
  }

  async function sendSecondCode(token: string) {
    setBusy(true);
    try {
      const r = await authApi.secondSend(token);
      setBusy(false);
      openCode('second', 'sms', r.maskedMobile ?? r.masked ?? null, r.resendInSeconds);
    } catch (err) {
      setBusy(false);
      const { status, body } = authError(err);
      if (status === 429) { openCode('second', 'sms', null, body.retryInSeconds); setCodeErr(tooManyText(body.retryInSeconds)); return; }
      codeFailure(err, 'We couldn’t send the code.');
    }
  }

  /** Drive the flow from a LoginResult. */
  function handleResult(r: LoginResult, viaCode: CodeChannel | null = null) {
    setBusy(false); setCodeErr(null); setStepError(null);
    // Older API / platform admins: LoginResponse without `step`.
    if (!r.step) {
      if (r.accessToken) {
        if (r.mustChangePassword) { setDoneToken(r.accessToken); setView('required'); return; }
        finish(r.accessToken); return;
      }
      if (r.mfaRequired && r.preMfaToken) {
        setLegacyMfa(r.preMfaToken); setMethods(['totp']); setCodeKey((k) => k + 1); setView('totp'); return;
      }
      setNotice({ kind: 'bad', text: OUTAGE_MSG }); setView('signin'); return;
    }
    if (r.pendingToken) setPendingToken(r.pendingToken);
    if (r.step === 'second') setTrustDays(r.trustDays ?? 0);
    if (r.trustToken && r.trustUntil) saveTrust(tenantSlug, { token: r.trustToken, until: r.trustUntil });   // v12.10.9.1 (#1288)
    switch (r.step) {
      case 'done':
        if (!r.accessToken) { setNotice({ kind: 'bad', text: OUTAGE_MSG }); setView('signin'); return; }
        if (r.mustChangePassword) { setDoneToken(r.accessToken); setView('required'); return; }
        finish(r.accessToken);
        return;
      case 'profile':
        setView('profile');
        return;
      case 'setup':
        setSetupInfo({
          canSkip: !!r.canSkip, graceDaysLeft: r.graceDaysLeft, enforceOn: r.enforceOn,
          methods: r.setupMethods && r.setupMethods.length ? r.setupMethods : ['totp', 'sms'],
        });
        setView('setup');
        return;
      case 'second': {
        const m: SecondMethod[] = r.methods && r.methods.length ? r.methods : ['sms'];
        setMethods(m);
        setFromCode(viaCode);
        // Authenticator first when the person has it (no text to wait for); else a texted code; else a backup code.
        if (m.includes('totp')) { setCodeKey((k) => k + 1); setView('totp'); return; }
        if (m.includes('sms')) { if (r.pendingToken) void sendSecondCode(r.pendingToken); return; }
        setCodeKey((k) => k + 1); setView('backup');
        return;
      }
      default:
        setNotice({ kind: 'bad', text: OUTAGE_MSG }); setView('signin');
    }
  }

  // ── L1 actions ───────────────────────────────────────────────────────────────────────────────────────────────────
  async function signIn() {
    setBusy(true); setNotice(null); setMismatch(false);
    try {
      typedPasswordRef.current = password;
      const r = await authApi.login(normalizeIdentifier(identifier), password, keep, tenantSlug);
      handleResult(r);
    } catch (err) {
      setBusy(false);
      typedPasswordRef.current = null;
      const { status, body } = authError(err);
      if (status === 423 || body.error === 'locked') { setPassword(''); goLocked(body.secondsLeft); return; }
      if (status === 403 && body.error === 'disabled') { setPassword(''); setView('disabled'); return; }
      if (status === 429 && body.error === 'too_many_attempts') { setPassword(''); setNotice({ kind: 'bad', text: String(body.message ?? 'Too many sign-in attempts from this network. Try again later.') }); setErrTick((t) => t + 1); return; }   // v12.10.9.1 (#1296)
      if (status === 429) { setPassword(''); goLocked(body.retryInSeconds ?? body.secondsLeft); return; }
      if (status === 401 || status === 403) { setPassword(''); setMismatch(true); setErrTick((t) => t + 1); return; }
      setNotice({ kind: 'bad', text: OUTAGE_MSG }); setErrTick((t) => t + 1);
    }
  }

  async function startCodeSignIn(channel: CodeChannel) {
    setCodeBusy(channel); setNotice(null); setMismatch(false);
    typedPasswordRef.current = null;
    try {
      const r = await authApi.codeLoginStart(normalizeIdentifier(identifier), channel, tenantSlug);
      setCodeBusy(null);
      openCode('codelogin', channel, r.masked ?? r.maskedMobile ?? null, r.resendInSeconds);
    } catch (err) {
      setCodeBusy(null);
      const { status, body } = authError(err);
      // v12.10.11.1 (#1470): a real account whose send failed is told so - never a silent "check your phone"
      setNotice({ kind: 'bad', text: status === 429 ? tooManyText(body.retryInSeconds) : body.error === 'send_failed' ? 'Technical error while sending the code. Please try another method.' : OUTAGE_MSG });
      setErrTick((t) => t + 1);
    }
  }

  // ── L4 actions ───────────────────────────────────────────────────────────────────────────────────────────────────
  async function verifyCode(code: string) {
    if (busy) return;
    if (codePurpose === 'reset') {
      setResetCode(code); setPwPurpose('reset_code'); setRejections([]); setPwError(null); setView('newpw');
      return;
    }
    setBusy(true); setCodeErr(null);
    try {
      if (codePurpose === 'codelogin') {
        const r = await authApi.codeLoginVerify(normalizeIdentifier(identifier), codeChannel, code, keep, tenantSlug);
        handleResult(r, codeChannel);
      } else if (codePurpose === 'second') {
        const r = await authApi.secondVerify(pendingToken, 'sms', code, wantTrust);
        handleResult(r, fromCode);
      } else {
        const r = await authApi.smsConfirm(pendingToken, code);
        setBusy(false);
        setBackupCodes(r.backupCodes ?? []); setAfterCodes(r.result ?? null); setView('backup_show');
      }
    } catch (err) {
      setBusy(false);
      codeFailure(err, 'That code isn’t right.');
    }
  }

  async function resend() {
    setCodeErr(null);
    try {
      let next: number | undefined;
      if (codePurpose === 'codelogin') next = (await authApi.codeLoginStart(normalizeIdentifier(identifier), codeChannel, tenantSlug)).resendInSeconds;
      else if (codePurpose === 'second') next = (await authApi.secondSend(pendingToken)).resendInSeconds;
      else if (codePurpose === 'setup_sms') next = (await authApi.smsStart(pendingToken, toE164(profileMobile) ?? profileMobile)).resendInSeconds;
      else await authApi.forgot(normalizeIdentifier(resetIdentifier), tenantSlug);
      setResendAt(Date.now() + (next ?? DEFAULT_RESEND_SECONDS) * 1000);
      setResent(true);
    } catch (err) {
      const { status, body } = authError(err);
      if (status === 429) { setCodeErr(tooManyText(body.retryInSeconds)); if (body.retryInSeconds) setResendAt(Date.now() + body.retryInSeconds * 1000); return; }
      codeFailure(err, 'We couldn’t send a new code.');
    }
  }

  // ── L4a / L4b ────────────────────────────────────────────────────────────────────────────────────────────────────
  async function verifyTotp(code: string) {
    if (busy) return;
    setBusy(true); setCodeErr(null);
    try {
      if (legacyMfa) {
        const r = await authApi.legacyMfaVerify(legacyMfa, code);
        setBusy(false);
        finish(r.accessToken);
        return;
      }
      const r = await authApi.secondVerify(pendingToken, 'totp', code, wantTrust);
      handleResult(r, fromCode);
    } catch (err) {
      setBusy(false);
      codeFailure(err, 'That code isn’t right.');
    }
  }

  async function verifyBackup(code: string) {
    if (busy) return;
    setBusy(true); setCodeErr(null);
    try {
      const r = await authApi.secondVerify(pendingToken, 'backup', code, wantTrust);
      handleResult(r, fromCode);
    } catch (err) {
      setBusy(false);
      const { status, body } = authError(err);
      if (status === 401 && body.error === 'bad_code') { setCodeErr('That backup code isn’t right or was already used.'); setCodeKey((k) => k + 1); return; }
      codeFailure(err, 'That backup code isn’t right or was already used.');
    }
  }

  // ── L13 ──────────────────────────────────────────────────────────────────────────────────────────────────────────
  async function saveProfile(first: string, last: string, mobile: string) {
    setBusy(true); setStepError(null);
    setNames({ first, last });
    if (mobile) setProfileMobile(mobile);
    try {
      const r = await authApi.profile(pendingToken, first, last, mobile ? (toE164(mobile) ?? mobile) : null);
      handleResult(r, fromCode);
    } catch (err) {
      setBusy(false);
      const { status, body } = authError(err);
      if (status === 401 || status === 410) { backToSignIn({ kind: 'bad', text: TIMED_OUT }); return; }
      setStepError(status === 400 ? (body.message ?? 'Check your name and mobile number, then try again.') : OUTAGE_MSG);
    }
  }

  // ── L5 / L5a / L5b ───────────────────────────────────────────────────────────────────────────────────────────────
  async function startSetup(method: SetupMethod, mobile: string) {
    setBusy(true); setStepError(null);
    try {
      if (method === 'totp') {
        const r = await authApi.totpStart(pendingToken);
        setBusy(false);
        setTotp(r); setCodeErr(null); setCodeKey((k) => k + 1); setView('setup_totp');
      } else {
        setProfileMobile(mobile);
        const r = await authApi.smsStart(pendingToken, toE164(mobile) ?? mobile);
        setBusy(false);
        openCode('setup_sms', 'sms', r.maskedMobile ?? r.masked ?? null, r.resendInSeconds);
      }
    } catch (err) {
      setBusy(false);
      const { status, body } = authError(err);
      if (status === 401 || status === 410) { backToSignIn({ kind: 'bad', text: TIMED_OUT }); return; }
      if (status === 429) { setStepError(tooManyText(body.retryInSeconds)); return; }
      setStepError(status === 400 ? (body.message ?? (method === 'sms' ? 'That mobile number can’t receive codes. Check it and try again.' : 'That didn’t work. Try again.')) : OUTAGE_MSG);
    }
  }

  async function skipSetup() {
    setBusy(true); setStepError(null);
    try { handleResult(await authApi.skipSetup(pendingToken)); }
    catch (err) {
      setBusy(false);
      const { status } = authError(err);
      if (status === 401 || status === 410) { backToSignIn({ kind: 'bad', text: TIMED_OUT }); return; }
      setStepError(status === 403 || status === 409 ? 'A second step is now required. Set it up to continue.' : OUTAGE_MSG);
    }
  }

  async function confirmTotp(code: string) {
    if (busy) return;
    setBusy(true); setCodeErr(null);
    try {
      const r = await authApi.totpConfirm(pendingToken, code);
      setBusy(false);
      setBackupCodes(r.backupCodes ?? []); setAfterCodes(r.result ?? null); setView('backup_show');
    } catch (err) {
      setBusy(false);
      codeFailure(err, 'That code isn’t right - check the time on your phone and try the next code.');
    }
  }

  function afterBackupCodes() {
    setBackupCodes([]);   // shown once
    if (afterCodes) { const r = afterCodes; setAfterCodes(null); handleResult(r, fromCode); return; }
    backToSignIn({ kind: 'ok', text: '2-step is set up. Sign in to continue.' });
  }

  // ── L6 / L6b ─────────────────────────────────────────────────────────────────────────────────────────────────────
  async function sendReset(id: string) {
    setBusy(true); setNotice(null);
    try {
      const r = await authApi.forgot(normalizeIdentifier(id), tenantSlug);
      setBusy(false);
      setResetIdentifier(id);
      setResetVia(r?.via ?? (id.includes('@') ? 'email' : 'sms'));
      setView('forgot_sent');
    } catch (err) {
      setBusy(false);
      const { status, body } = authError(err);
      setNotice({ kind: 'bad', text: status === 429 ? tooManyText(body.retryInSeconds) : body.error === 'send_failed' ? 'Technical error while sending the code. Please try another method.' : OUTAGE_MSG });   // v12.10.11.1 (#1470)
    }
  }

  // ── v12.10.9.1 (#1275) Contact support ─────────────────────────────────────────────────────────────────────────────
  // v12.10.11.1 (#1460): the answer says created / existing (same email or mobile, request still open) / limited (3 today).
  type SupportAnswer = { status?: SupportOutcome; reference?: string; contact?: string; sentAt?: string | null; count?: number };
  const [support, setSupport] = useState<{ contact: string; reference: string; mode: SupportOutcome; sentAt?: string | null; count?: number; draft?: string } | null>(null);
  async function sendSupport(f: SupportFields) {
    setBusy(true); setNotice(null);
    try {
      const r = await api.post<SupportAnswer>('/public/support-request',
        { name: f.name || null, practice: f.practice || null, slug: tenantSlug || null, contact: f.contact, message: f.message });
      setBusy(false);
      // 'existing': what they just typed is offered in the "Anything to add?" box, so it is not lost - nothing is stored until they add it.
      setSupport({ contact: r?.contact ?? f.contact, reference: r?.reference ?? '', mode: r?.status ?? 'created', sentAt: r?.sentAt, count: r?.count,
        draft: r?.status === 'existing' ? f.message : undefined });
      setView('support_sent');
    } catch (err) {
      setBusy(false);
      const { status } = authError(err);
      setNotice({ kind: 'bad', text: status === 429
        ? 'Too many requests from this network. Try again in an hour, or email support@voicespectrum.ai.'
        : 'We couldn’t send that just now. Try again, or email support@voicespectrum.ai.' });
    }
  }
  // v12.10.11.1 (#1460 Q2): "Add to SR-xxxx" - the text joins the same request; support@ gets one "SR-xxxx updated" email.
  async function addToSupport(text: string) {
    if (!support) return;
    setBusy(true); setNotice(null);
    try {
      const r = await api.post<SupportAnswer>('/public/support-request/add', { reference: support.reference, contact: support.contact, message: text });
      setBusy(false);
      setSupport({ ...support, mode: r?.status === 'limited' ? 'limited' : 'added', count: r?.count ?? support.count, draft: undefined });
    } catch (err) {
      setBusy(false);
      const { status } = authError(err);
      setNotice({ kind: 'bad', text: status === 429
        ? 'Too many requests from this network. Try again in an hour, or email support@voicespectrum.ai.'
        : status === 404 ? 'That request is no longer open. Go back and send a new one.'
        : 'We couldn’t add that just now. Try again, or email support@voicespectrum.ai.' });
    }
  }

  // ── L7 / L8 / L10 save ───────────────────────────────────────────────────────────────────────────────────────────
  async function savePassword(v: { password: string; current: string; first: string; last: string; mobile: string }) {
    setBusy(true); setPwError(null);
    try {
      if (pwPurpose === 'invite') {
        setNames({ first: v.first, last: v.last });
        if (v.mobile) setProfileMobile(v.mobile);
        const r = await authApi.acceptInvite({
          token: urlToken, firstName: v.first, lastName: v.last, password: v.password,
          ...(v.mobile ? { mobile: toE164(v.mobile) ?? v.mobile } : {}),
        });
        handleResult(r);
        return;
      }
      if (pwPurpose === 'required') {
        const old = typedPasswordRef.current ?? v.current;
        await authApi.changePassword(doneToken, old, v.password);
        setBusy(false);
        finish(doneToken);
        return;
      }
      if (pwPurpose === 'reset_token') await authApi.reset({ token: urlToken, newPassword: v.password });
      else await authApi.reset({ identifier: normalizeIdentifier(resetIdentifier), code: resetCode, newPassword: v.password, ...(tenantSlug ? { tenantSlug } : {}) });
      setBusy(false);
      if (mode === 'login') {
        backToSignIn({ kind: 'ok', text: 'Password changed. You were signed out on your other devices. Sign in with your new password.' });
        setIdentifier(resetIdentifier);
      } else {
        navigate('/login', { replace: true, state: { notice: 'pwchanged' } });
      }
    } catch (err) {
      setBusy(false);
      const { status, body } = authError(err);
      if (status === 400 && body.error === 'rules') {
        setRejections((r) => [...r, { password: v.password, failed: body.failed ?? [] }]);
        setPwError(<Banner kind="bad">{body.message ?? 'That password doesn’t meet the rules below. Choose another.'}</Banner>);
        return;
      }
      if (status === 400 && body.error === 'invalid_or_expired') {
        setPwError(
          <Banner kind="bad">
            {pwPurpose === 'reset_code' ? 'That code has expired or isn’t right.' : 'This reset link has expired or was already used.'}{' '}
            <button type="button" className="vsl-lnk vsl-lnk--inline" onClick={() => { setPwError(null); setNotice(null); setView('forgot'); }}>Send a new one</button>
          </Banner>,
        );
        return;
      }
      if (pwPurpose === 'invite' && status === 410) { setInviteProblem(body.error === 'expired' ? 'expired' : 'invalid'); setView('invite_problem'); return; }
      if (pwPurpose === 'required' && (status === 400 || status === 401) && body.error !== 'session_ended' && !typedPasswordRef.current) {
        setPwError(<Banner kind="bad">Your current password isn’t right.</Banner>);
        return;
      }
      if (pwPurpose === 'required' && status === 401) { backToSignIn({ kind: 'bad', text: TIMED_OUT }); return; }
      setPwError(<Banner kind="bad">{OUTAGE_MSG}</Banner>);
    }
  }

  // ── render ───────────────────────────────────────────────────────────────────────────────────────────────────────
  const noticeEl = notice ? <Banner kind={notice.kind}>{notice.text}</Banner> : null;
  const personal = useMemo(() => {
    const out: string[] = [];
    const add = (s: string | undefined) => {
      if (!s) return;
      const t = s.trim();
      if (!t) return;
      out.push(t);
      if (t.includes('@')) out.push(t.split('@')[0]);
    };
    add(pwPurpose === 'reset_code' ? resetIdentifier : identifier);
    add(invite?.identifier);
    add(names.first); add(names.last);
    return out;
  }, [pwPurpose, resetIdentifier, identifier, invite, names]);

  let body: ReactNode;
  switch (view) {
    case 'signin':
      body = (
        <SignInView org={org} identifier={identifier} onIdentifier={(v) => { setIdentifier(v); setMismatch(false); }}
          password={password} onPassword={setPassword} keep={keep} onKeep={setKeep}
          notice={noticeEl} mismatch={mismatch} errTick={errTick} busy={busy} codeBusy={codeBusy}
          onSignIn={signIn} onCode={startCodeSignIn} onForgot={() => { setNotice(null); setView('forgot'); }}
          onSupport={() => { setNotice(null); setView('support'); }} />
      );
      break;
    case 'locked':
      body = <LockedView org={org} until={lockUntil} onDone={() => backToSignIn()} onReset={() => { setNotice(null); setView('forgot'); }} onBack={() => backToSignIn()} />;
      break;
    case 'code':
      body = (
        <CodeView org={org} purpose={codePurpose} channel={codeChannel} masked={codeMasked} resendAt={resendAt} resent={resent}
          typed={codePurpose === 'codelogin' ? identifier.trim() : codePurpose === 'reset' ? resetIdentifier.trim() : undefined}
          error={codeErr} errKey={codeKey} busy={busy}
          altMethods={methods.filter((m) => m !== 'sms')} trust={codePurpose === 'second' ? trust : undefined}
          onVerify={verifyCode} onResend={resend}
          onAlt={(m) => { setCodeErr(null); setCodeKey((k) => k + 1); setView(m === 'totp' ? 'totp' : 'backup'); }}
          onBack={codePurpose === 'setup_sms' ? () => { setStepError(null); setView('setup'); } : () => backToSignIn()} />
      );
      break;
    case 'totp':
      body = (
        <TotpView org={org} fromTextCode={fromCode === 'sms' && !legacyMfa} canText={!legacyMfa && methods.includes('sms')} canBackup={!legacyMfa && methods.includes('backup')}
          error={codeErr} errKey={codeKey} busy={busy} onVerify={verifyTotp} trust={legacyMfa ? undefined : trust}
          onText={() => { setCodeErr(null); void sendSecondCode(pendingToken); }}
          onBackup={() => { setCodeErr(null); setCodeKey((k) => k + 1); setView('backup'); }}
          onBack={() => backToSignIn()} />
      );
      break;
    case 'backup':
      body = (
        <BackupView org={org} error={codeErr} errKey={codeKey} busy={busy} onVerify={verifyBackup} trust={trust}
          onBack={() => {
            setCodeErr(null); setCodeKey((k) => k + 1);
            if (methods.includes('totp')) setView('totp');
            else if (methods.includes('sms')) void sendSecondCode(pendingToken);
            else backToSignIn();
          }} />
      );
      break;
    case 'setup':
      body = (
        <SetupView org={org} canSkip={setupInfo.canSkip} graceDaysLeft={setupInfo.graceDaysLeft} enforceOn={setupInfo.enforceOn}
          methods={setupInfo.methods} initialMobile={profileMobile} error={stepError} busy={busy} skipBusy={false}
          onContinue={startSetup} onSkip={skipSetup} />
      );
      break;
    case 'setup_totp':
      body = totp
        ? <SetupTotpView org={org} secret={totp.secret} otpauthUri={totp.otpauthUri} error={codeErr} errKey={codeKey} busy={busy}
            onVerify={confirmTotp} onBack={() => { setCodeErr(null); setView('setup'); }} />
        : null;
      break;
    case 'backup_show':
      body = <BackupCodesView org={org} codes={backupCodes} busy={busy} onContinue={afterBackupCodes} />;
      break;
    case 'profile':
      body = <ProfileView org={org} error={stepError} busy={busy} onContinue={saveProfile} />;
      break;
    case 'support':
      body = <SupportView org={org} notice={noticeEl} busy={busy} onSend={sendSupport} onBack={() => backToSignIn()}
        initial={{ name: '', practice: tenantSlug ? org : '', contact: identifier, message: '' }} />;
      break;
    case 'support_sent':
      body = <SupportSentView org={org} contact={support?.contact ?? ''} reference={support?.reference ?? ''} onBack={() => backToSignIn()}
        mode={support?.mode} sentAt={support?.sentAt} count={support?.count} draft={support?.draft} busy={busy} notice={noticeEl} onAdd={(t) => void addToSupport(t)} />;
      break;
    case 'forgot':
      body = <ForgotView org={org} initial={resetIdentifier || identifier} notice={noticeEl} busy={busy} onSend={sendReset} onBack={() => backToSignIn()} />;
      break;
    case 'forgot_sent':
      body = (
        <ForgotSentView org={org} via={resetVia} typed={resetIdentifier.trim()} onBack={() => backToSignIn()}
          onEnterCode={() => openCode('reset', 'sms', null, DEFAULT_RESEND_SECONDS)} />
      );
      break;
    case 'newpw':
      body = (
        <NewPasswordView key={pwPurpose} org={org} purpose={pwPurpose} minLength={rules.minLength} reuse={rules.reuse} charMix={rules.charMix} context={[...rules.context, org]}
          personal={personal} rejections={rejections} needCurrent={pwPurpose === 'required' && !typedPasswordRef.current}
          invite={invite ? { groupNames: invite.groupNames ?? [], identifier: invite.identifier } : undefined}
          prefill={invite ? { first: invite.firstName, last: invite.lastName, mobile: invite.mobile } : undefined}
          error={pwError} busy={busy} onSave={savePassword} />
      );
      break;
    case 'required':
      body = <RequiredView org={org} onContinue={() => { setPwPurpose('required'); setRejections([]); setPwError(null); setView('newpw'); }} />;
      break;
    case 'disabled':
      body = <DisabledView org={org} onBack={() => backToSignIn()} />;
      break;
    case 'invite_loading':
      body = <LoadingView org={org} />;
      break;
    case 'invite_problem':
      body = <InviteProblemView org={org} kind={urlToken ? inviteProblem : 'invalid'} onSignIn={() => backToSignIn()} onRetry={() => setInviteTry((t) => t + 1)} />;
      break;
    default:
      body = null;
  }

  // v12.10.9.3 (#1312): under the Sign in card - Contact support and the compliance badges
  const below = view === 'signin' ? <BelowCard onSupport={() => { setNotice(null); setView('support'); }} /> : undefined;
  return <LoginFrame orgName={org} below={below}><div key={view} className="vsl-view">{body}</div></LoginFrame>;
}
